/**
 * Take payment (DNKADM-0402 U5) — the small dialog that settles ONE unpaid booking:
 * a card (saved or entered at the desk) or money taken at the counter.
 *
 * **It is the only place the console charges an unpaid booking, and it charges exactly
 * one** (Operator guardrail, 2026-09-22): there is no bulk action, no "charge all
 * unpaid", and check-in never charges. Staff choose the tender and press the button;
 * nothing happens otherwise.
 *
 * The amount is the booking's own `amountDue` — server-owned, displayed here and never
 * sent. The tender controls are the SAME shared ones the create dialog uses
 * ({@link ReservationTenderFields} over `reservationTender.ts`), not a copy.
 *
 * Refusals map through the shared reservations error copy. `409 RESERVATION_NOT_UNPAID`
 * means somebody else already took the money: the booking is re-read so the surface
 * behind this dialog shows the truth, and the dialog says so plainly instead of
 * offering a charge that cannot happen.
 *
 * **An unknown outcome locks the dialog (DNKADM-0475, the DNKADM-0467 rule).** A network
 * error, timeout or 5xx may still have taken the money, so the key and the exact body are
 * kept, the tender is locked, and only "Check again" (the same body under the same key,
 * which dink-api answers without charging twice) can settle it. Closing asks first.
 */
import { useEffect, useId, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { Icon, resolveTokens } from '@/theme';
import {
  getReservation,
  takeReservationPayment,
  type Reservation,
  type TakeReservationPaymentInput,
} from '@/api/reservations';
import { CardTokenizationError, newIdempotencyKey } from '@/api/payments';
import { useOptionalOperator } from '@/auth/authHooks';
import { scrubBrand } from '@/utils/whitelabel';
import { amountDueLabel } from './calendar/derive';
import { isAlreadyPaidRefusal, toTakePaymentErrorMessage } from './reservationsError';
import { useReservationTender, type TenderChoice } from './reservationTender';
import { ReservationTenderFields } from './ReservationTenderFields';
import {
  outcomeIsUnknown,
  UNCONFIRMED_PAYMENT_CLOSE_PROMPT,
  unconfirmedMessage,
} from '@/features/members/unconfirmedOutcome';

interface TakePaymentDialogProps {
  open: boolean;
  /** The unpaid booking this payment settles. */
  reservation: Reservation;
  onClose: () => void;
  /**
   * The payment landed: the server's updated (now `paid`) reservation plus the toast.
   * The host replaces its copy in place, exactly as it does for every other write.
   */
  onPaid: (updated: Reservation, message: string) => void;
  /**
   * Somebody else settled it first — the freshly re-read booking, so the surface
   * behind this dialog stops showing Unpaid. No toast: the dialog says it in place.
   */
  onRefreshed: (updated: Reservation) => void;
}

export function TakePaymentDialog({
  open,
  reservation,
  onClose,
  onPaid,
  onRefreshed,
}: TakePaymentDialogProps) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const ids = useId();
  const titleId = `${ids}-title`;
  const choiceId = `${ids}-tender`;
  const operator = useOptionalOperator();
  const tenantSlug = operator?.tenantSlug?.trim() ?? '';

  const [choice, setChoice] = useState<TenderChoice>('card');
  const tender = useReservationTender(choice);
  const [error, setError] = useState<string | null>(null);
  const [settled, setSettled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // One key per attempt, kept across a retry of that attempt (a replay returns the
  // same result and never charges twice), dropped when the tender changes.
  const attemptKeyRef = useRef<string | null>(null);
  // DNKADM-0475: the body of a payment the server never confirmed either way, kept so
  // "Check again" re-sends it unchanged (same key, same card nonce or method).
  const [unconfirmed, setUnconfirmed] = useState<TakeReservationPaymentInput | null>(null);
  const locked = submitting || unconfirmed !== null;
  useEffect(() => {
    attemptKeyRef.current = null;
  }, [choice, tender.cardOnFileId, tender.paymentMethodId]);

  const uuid = reservation.reservationUuid;
  const amount = amountDueLabel(reservation);
  const methodLabel = scrubBrand(tender.selectedMethod?.label ?? null);

  function close() {
    // A charge in flight is un-dismissable: closing mid-submit would orphan the
    // outcome of a call that has already left the browser.
    if (submitting) return;
    // DNKADM-0475: a payment that may still land is never left silently.
    if (unconfirmed !== null && !window.confirm(UNCONFIRMED_PAYMENT_CLOSE_PROMPT)) return;
    onClose();
  }

  async function submit() {
    if (!uuid) return;
    setError(null);
    let payment;
    try {
      payment = await tender.buildPayment();
    } catch (err) {
      setError(
        err instanceof CardTokenizationError
          ? err.validation
            ? 'Finish entering the card before taking payment.'
            : 'The card could not be read. Check the card details and try again.'
          : 'The payment details are incomplete.',
      );
      return;
    }
    const key = attemptKeyRef.current ?? newIdempotencyKey();
    attemptKeyRef.current = key;
    await send(uuid, { funding: choice, payment, idempotencyKey: key });
  }

  /** DNKADM-0475: settle an unconfirmed payment by re-sending its own body and key. */
  async function checkAgain() {
    if (!uuid || unconfirmed === null || submitting) return;
    setError(null);
    await send(uuid, unconfirmed);
  }

  async function send(uuid: string, input: TakeReservationPaymentInput) {
    setSubmitting(true);
    try {
      const updated = await takeReservationPayment(uuid, input);
      attemptKeyRef.current = null;
      setUnconfirmed(null);
      onPaid(updated, takenMessage(input.funding, amount, methodLabel));
    } catch (err) {
      if (outcomeIsUnknown(err)) {
        // The money may have moved: keep the key and the body, lock the tender.
        setUnconfirmed(input);
        return;
      }
      setUnconfirmed(null);
      if (isAlreadyPaidRefusal(err)) {
        // Nothing was charged. Re-read the booking so the caller's copy is the truth,
        // and stop offering a payment that cannot be taken.
        attemptKeyRef.current = null;
        setSettled(true);
        setError(toTakePaymentErrorMessage(err));
        try {
          onRefreshed(await getReservation(uuid));
        } catch {
          // The re-read is a courtesy; the message above is the outcome.
        }
        return;
      }
      setError(toTakePaymentErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth aria-labelledby={titleId}>
      <DialogTitle id={titleId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Take payment</Typography>
          <Typography variant="body2">
            {amount === null
              ? 'Settle this booking.'
              : `Unpaid · ${amount} — take it now to mark this booking paid.`}
          </Typography>
        </Box>
        <IconAction size="small" onClick={close} label="Close">
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>

      <DialogContent>
        <Stack sx={{ gap: 1.5, mt: 0.5 }}>
          <RadioGroup
            aria-labelledby={choiceId}
            value={choice}
            onChange={(_event, next) => {
              tender.reset();
              setChoice(next === 'external' ? 'external' : 'card');
            }}
          >
            <Typography id={choiceId} sx={{ fontSize: 13, fontWeight: 600, color: t.text2 }}>
              How are they paying?
            </Typography>
            <FormControlLabel
              value="card"
              control={<Radio size="small" />}
              disabled={locked || settled}
              label={<Typography sx={{ fontSize: 14 }}>Card</Typography>}
            />
            <FormControlLabel
              value="external"
              control={<Radio size="small" />}
              disabled={locked || settled}
              label={<Typography sx={{ fontSize: 14 }}>Cash or terminal</Typography>}
            />
          </RadioGroup>

          {/* A booking with no linked contact still takes a payment: cash needs no member
              at all, and a card is entered at the desk (the saved-card chooser is the only
              thing a missing contact costs). The server resolves the member from the
              booking either way, so nothing here is a dead end with a permanently disabled
              button and no reason given. */}
          {!settled && (
            <ReservationTenderFields
              choice={choice}
              contactId={reservation.contactId}
              tenantSlug={tenantSlug}
              locationRef={reservation.locationId ?? ''}
              tender={tender}
              disabled={locked}
            />
          )}

          {unconfirmed !== null && (
            <Alert
              severity="warning"
              role="alert"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void checkAgain()}
                  disabled={submitting}
                >
                  Check again
                </Button>
              }
            >
              {unconfirmedMessage(unconfirmed.funding)}
            </Alert>
          )}

          {error && (
            <Alert severity={settled ? 'info' : 'error'} role="alert">
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={close} disabled={submitting} sx={{ color: t.text2 }}>
          {settled ? 'Close' : 'Cancel'}
        </Button>
        {!settled && (
          <Button
            variant="contained"
            startIcon={
              submitting ? <CircularProgress size={16} color="inherit" /> : <Icon name="check" />
            }
            onClick={() => void submit()}
            disabled={locked || !tender.ready || !uuid}
          >
            {submitting ? 'Taking payment…' : 'Take payment'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** "Payment taken: $60.00 by card" / "Payment taken: $60.00 recorded as Cash". */
function takenMessage(
  choice: TenderChoice,
  amount: string | null,
  methodLabel: string | null,
): string {
  const how = choice === 'card' ? 'by card' : `recorded as ${methodLabel ?? 'paid at the desk'}`;
  return amount === null ? `Payment taken ${how}.` : `Payment taken: ${amount} ${how}.`;
}
